import { Carousel } from '@components/Carousel';
import { useLocale } from '@components/Contexts/LocaleContext';
import { useTailwindBreakpoint } from '@components/Contexts/TailwindBreakpointContext';
import Image from 'next/image';
import { useEffect, useState } from 'react';

const filters = {
  blue: 'linear-gradient(180deg, rgba(29, 29, 29, 0.90) 0%, rgba(41, 173, 206, 0.90) 100%)',
  black: 'linear-gradient(180deg, rgba(29, 29, 29, 0.8) 0%, #1D1D1D 100%)',
};

const cards = [
  {
    icon: { src: '/images/ememo/infinity.svg', alt: 'infinity' },
    background: {
      src: '/images/ememo/nolimit-bg.webp',
      filter: filters.blue,
    },
    title: {
      en: 'No Limit',
      th: 'ใช้งานไม่จำกัด',
    },
    details: [
      {
        en: 'Unlimited Users',
        th: 'ไม่จำกัดจำนวนผู้ใช้งาน',
      },
      {
        en: 'Unlimited Documents',
        th: 'ไม่จำกัดจำนวนเอกสาร',
      },
      {
        en: 'Unlimited LOA/DOA',
        th: 'ไม่จำกัดสายอนุมัติ LOA/DOA',
      },
    ],
  },
  {
    icon: { src: '/images/ememo/paintcan.svg', alt: 'paintcan' },
    background: {
      src: '/images/ememo/customization-bg.webp',
      filter: filters.black,
    },
    title: {
      en: 'Customization & White-labeling',
      th: 'ปรับแต่งได้ตามต้องการ และรองรับการทำ White-label',
    },
    details: [
      {
        en: 'Customizable interface with your theme and logo',
        th: 'ปรับแต่งธีมสีและโลโก้ได้ตามต้องการ',
      },
      {
        en: 'Initial setup of approval chains',
        th: 'ตั้งค่าลำดับการอนุมัติเบื้องต้นได้อย่างง่ายดาย',
      },
    ],
  },
  {
    icon: {
      src: '/images/ememo/customer-support.svg',
      alt: 'customer-support',
    },
    background: {
      src: '/images/ememo/implementation-bg.webp',
      filter: filters.blue,
    },
    title: {
      en: 'Implementation, Training & Support',
      th: 'บริการติดตั้งระบบ พร้อมอบรมและดูแลอย่างใกล้ชิด',
    },
    details: [
      {
        en: 'Two training sessions',
        th: 'อบรมการใช้งานระบบ 2 ครั้ง',
      },
      {
        en: 'Two week UAT',
        th: <>ทดลองการใช้งานระบบ UAT <br />2 สัปดาห์</>,
      },
      {
        en: 'After-sales technical support via call center',
        th: 'บริการหลังการขาย พร้อมทีมเทคนิคคอยให้คำปรึกษาทาง Call Center',
      },
    ],
  },
  {
    icon: { src: '/images/ememo/multiple-pages.svg', alt: 'multiple-pages' },
    background: {
      src: '/images/ememo/included-forms-bg.webp',
      filter: filters.black,
    },
    title: {
      en: 'Included Forms',
      th: 'แบบฟอร์มสำเร็จรูป พร้อมใช้งานทันที',
    },
    details: [
      {
        en: 'Internal approval form',
        th: 'แบบฟอร์มการขออนุมัติภายใน',
      },
      {
        en: 'External approval form',
        th: 'แบบฟอร์มการขออนุมัติภายนอก',
      },
      {
        en: 'Procurement Form',
        th: 'แบบฟอร์มการขออนุมัติการซื้อ',
      },
    ],
  },
];

const ValueOfMoney = () => {
  const { lang } = useLocale();
  const { screenWidth } = useTailwindBreakpoint();
  const [initWidth, setInitWidth] = useState(null);

  useEffect(() => {
    setInitWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    if (screenWidth) {
      setInitWidth(null);
    }
  }, [screenWidth]);

  return (
    <div className="flex flex-col">
      <h2 className="text-4xl xl:text-xl font-bold gradient-head text-center my-[40px]">
        Value of Money
      </h2>
      <div>
        <Carousel
          justify={
            initWidth >= 1280 || screenWidth >= 1280 ? 'center' : 'start'
          }
        >
          {cards.map(({ icon, background, title, details }) => (
            <div
              key={title.en}
              className={`relative bg-no-repeat bg-cover bg-center w-[270px] min-h-[335px] h-full rounded-[40px] p-[10px] text-white`}
              style={{
                background: `${background.filter}, url(${background.src})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            >
              <div className="flex flex-col">
                <div className="flex justify-center rounded-full bg-white w-[56px] h-[56px] mb-[16px]">
                  <Image alt={icon.alt} src={icon.src} width={36} height={36} />
                </div>
                <p className="text-[18px] font-semibold mb-[24px] h-[56px]">
                  {lang === 'en' ? title.en : title.th}
                </p>
                <ul className="flex flex-col gap-[12px]">
                  {details.map((detail) => (
                    <li key={detail.en} className="flex gap-2">
                      <div className="min-w-[24px] -mt-[2px]">
                        <Image
                          alt="check"
                          src="/images/ememo/check-round-fill.svg"
                          width={24}
                          height={24}
                        />
                      </div>
                      <p className="text-[16px] font-light">
                        {lang === 'en' ? detail.en : detail.th}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export default ValueOfMoney;

// style={{ background:
//   'linear-gradient(180deg, rgba(29, 29, 29, 0.90) 0%, rgba(41, 173, 206, 0.90) 100%),
//   url(${background.src})',
// backgroundSize: 'cover',
// backgroundRepeat: 'no-repeat',
// backgroundPosition: 'center'
//  }}
